import type { AuthorBylineConfig } from 'interfaces/content/AuthorByline';
import { useEffect, useState } from 'react';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { Contributor, ContributorName } from 'components/Contributors/Contributor';
import * as S from './AuthorsBylineStyles';
import { useOptimizely } from 'providers/OptimizelyProvider';

const CompactByline = ({ authors, reviewedBy, trackingData, dataCy, isFWire }: AuthorBylineConfig) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleContributors = authors.slice(0, 2);
  const { userFlags: userVariants } = useOptimizely();
  const variants = userVariants?.socialShareV2 as { variant: string; enabled: boolean };
  const variantToUse = variants?.variant;
  const enabled = variantToUse !== 'off' && variants?.enabled;
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const isEnabled = isClient ? enabled || false : false;
  const moreAuthors = authors.slice(2);
  const showMoreContributors = authors.length > 2;

  return (
    <S.CompactBylineWrapper data-cy={dataCy}>
      <div>
        <S.CollapsedView
          $isExpanded={isExpanded || isFWire}
          $hasExpanded={moreAuthors.length !== 0}
          className={isEnabled ? 'socialShare' : ''}
        >
          {visibleContributors.map((contributor, index) => (
            <div
              className='author-item'
              key={contributor.uri}
            >
              <ContributorName
                contributor={contributor}
                type={reviewedBy && index === 1 ? 'reviewer' : 'author'}
                showDetails
                trackingData={trackingData}
              />
            </div>
          ))}
        </S.CollapsedView>

        <S.ExpandedView $isVisible={isExpanded || isFWire}>
          {moreAuthors.map((contributor, index) => (
            <div
              className='author-item'
              key={contributor.uri}
            >
              <ContributorName
                contributor={contributor}
                type={reviewedBy && index === 1 ? 'reviewer' : 'author'}
                showDetails
                trackingData={trackingData}
              />
            </div>
          ))}
        </S.ExpandedView>
      </div>
      {!isFWire && showMoreContributors && (
        <S.SeeMoreButton
          onClick={() => setIsExpanded(!isExpanded)}
          type='button'
          variant='tertiary'
          arrowType={isExpanded ? 'up-arrow' : 'down-arrow'}
          data-cy={COMMON_TEST_IDS.SEE_MORE_AUTHORS_BUTTON}
        />
      )}
    </S.CompactBylineWrapper>
  );
};
const DesktopByline = ({ authors, trackingData, dataCy, reviewedBy, isFWire }: AuthorBylineConfig) => {
  const [isOpen, setIsOpen] = useState(false);
  const visibleContributors = authors.slice(0, 2);
  const moreAuthors = authors.slice(2);
  const seeMoreAuthors = moreAuthors.length > 0;
  return (
    <S.DesktopBylineWrapper data-cy={dataCy}>
      <S.VisibleContributorsWrapper data-cy={dataCy}>
        {visibleContributors.map((contributor) => (
          <Contributor
            key={contributor.uri}
            contributor={contributor}
            type={reviewedBy && contributor.uri === reviewedBy.uri ? 'reviewer' : 'author'}
            trackingData={trackingData}
          />
        ))}
        {!isFWire && seeMoreAuthors && (
          <S.SeeMoreButton
            onClick={() => setIsOpen(!isOpen)}
            type='button'
            variant='tertiary'
            arrowType={isOpen ? 'up-arrow' : 'down-arrow'}
            data-cy={COMMON_TEST_IDS.SEE_MORE_AUTHORS_BUTTON}
          >
            SEE MORE
          </S.SeeMoreButton>
        )}
      </S.VisibleContributorsWrapper>

      {(isOpen || isFWire) && (
        <S.SeeMoreAuthorsWrapper>
          {moreAuthors.map((contributor) => (
            <Contributor
              key={contributor.uri}
              contributor={contributor}
              type='author'
              trackingData={trackingData}
            />
          ))}
        </S.SeeMoreAuthorsWrapper>
      )}
    </S.DesktopBylineWrapper>
  );
};

const AuthorsByline = ({ authors, trackingData, dataCy, reviewedBy, isFWire }: AuthorBylineConfig) => {
  return (
    <>
      <CompactByline
        authors={authors}
        reviewedBy={reviewedBy}
        trackingData={trackingData}
        dataCy={dataCy}
        isFWire={isFWire}
      />
      <DesktopByline
        authors={authors}
        reviewedBy={reviewedBy}
        trackingData={trackingData}
        dataCy={dataCy}
        isFWire={isFWire}
      />
    </>
  );
};

export default withErrorBoundary(AuthorsByline, { fragment: UIFragments.CONTRIBUTORS_BYLINE });
